import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { FileText, Copy, Download, Printer, CheckCircle, AlertCircle, ChevronDown, ExternalLink } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const SynopsisGenerator = () => {
  const { darkMode } = useTheme();
  
  const [formData, setFormData] = useState({
    noticeType: '',
    title: '',
    contractingActivity: '',
    naicsCode: '',
    naicsDescription: '',
    setAsideType: '',
    estimatedDollarValue: '',
    periodOfPerformance: '',
    description: '',
    responseDeadline: '',
    responseTime: '2:00 PM ET',
    pocName: '',
    pocTitle: '',
    pocEmail: '',
    pocPhone: '',
    vaarAuthorityNote: 'VAAR 819.7003 - Veterans First Contracting Program',
    additionalInstructions: ''
  });

  const [generatedNotice, setGeneratedNotice] = useState('');
  const [showValidation, setShowValidation] = useState(false);
  const [copied, setCopied] = useState(false);
  const [synopsisLoading, setSynopsisLoading] = useState(false);
  const [synopsisAiOutput, setSynopsisAiOutput] = useState('');
  const [synopsisAccepted, setSynopsisAccepted] = useState(false);
  const [fromJA, setFromJA] = useState(false);

  // Load pre-filled data from J&A approval if available
  useEffect(() => {
    try {
      const jaData = sessionStorage.getItem('ja_to_synopsis');
      if (jaData) {
        const d = JSON.parse(jaData);
        setFormData(prev => ({
          ...prev,
          title: d.title || prev.title,
          naicsCode: d.naicsCode || prev.naicsCode || '',
          naicsDescription: d.naicsDescription || prev.naicsDescription || '',
          setAsideType: d.setAsideType || prev.setAsideType || '',
          estimatedDollarValue: d.estimatedDollarValue || prev.estimatedDollarValue || '',
          periodOfPerformance: d.periodOfPerformance || prev.periodOfPerformance || '',
          description: d.description || prev.description,
          contractingActivity: d.contractingActivity || prev.contractingActivity,
          pocName: d.pocName || prev.pocName || '',
          pocTitle: d.pocTitle || prev.pocTitle || '',
          pocEmail: d.pocEmail || prev.pocEmail || '',
          pocPhone: d.pocPhone || prev.pocPhone || '',
        }));
        setFromJA(true);
        sessionStorage.removeItem('ja_to_synopsis');
      }
    } catch(e) {}
  }, []);



  const noticeTypes = [
    { value: 'Sources Sought', label: 'Sources Sought' },
    { value: 'Pre-Solicitation', label: 'Pre-Solicitation' },
    { value: 'Combined Synopsis/Solicitation', label: 'Combined Synopsis/Solicitation' }
  ];

  const setAsideTypes = [
    'Full & Open',
    'SDVOSB Set-Aside',
    'VOSB Set-Aside',
    'Small Business',
    '8(a)',
    'HUBZone',
    'WOSB',
    'Sole Source'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const generateNotice = () => {
    setShowValidation(true);
    
    const requiredFields = [
      'noticeType', 'title', 'contractingActivity', 'naicsCode', 
      'setAsideType', 'description', 'responseDeadline', 'pocName', 'pocEmail'
    ];
    
    const isValid = requiredFields.every(field => formData[field]);
    
    if (!isValid) return;
    
    const now = new Date();
    const year = now.getFullYear();
    const random4Digits = Math.floor(1000 + Math.random() * 9000);
    
    const noticeNumber = `VA-${formData.naicsCode}-${year}-${random4Digits}`;
    
    const notice = `---
${formData.noticeType} — ${formData.title}
Solicitation Number: ${noticeNumber}
Contracting Activity: ${formData.contractingActivity}
NAICS Code: ${formData.naicsCode} — ${formData.naicsDescription}
Set-Aside: ${formData.setAsideType}
Estimated Value: ${formData.estimatedDollarValue}
Period of Performance: ${formData.periodOfPerformance}

DESCRIPTION:
${formData.description}

RESPONSES DUE: ${formData.responseDeadline} at ${formData.responseTime}

INSTRUCTIONS: Interested parties should submit capability statements / responses to:
${formData.pocName}, ${formData.pocTitle}
Email: ${formData.pocEmail}
Phone: ${formData.pocPhone}

${formData.vaarAuthorityNote}

${formData.additionalInstructions ? `${formData.additionalInstructions}\n` : ''}

This notice is issued in accordance with Federal Acquisition Regulation (FAR) Part 5 and the Veterans Affairs Acquisition Regulation (VAAR).
---`;
    
    setGeneratedNotice(notice);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedNotice);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadNotice = () => {
    const blob = new Blob([generatedNotice], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `synopsis-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const printNotice = () => {
    window.print();
  };

  const getWordCount = (text) => {
    return text.trim() ? text.trim().split(/\s+/).length : 0;
  };

  const getCharacterCount = (text) => {
    return text.length;
  };

  const isFieldRequired = (field) => {
    const requiredFields = [
      'noticeType', 'title', 'contractingActivity', 'naicsCode', 
      'setAsideType', 'description', 'responseDeadline', 'pocName', 'pocEmail'
    ];
    return requiredFields.includes(field);
  };

  const isFieldValid = (field) => {
    if (!showValidation) return true;
    return !isFieldRequired(field) || formData[field];
  };

  const getPreviewBadgeColor = () => {
    const requiredFields = [
      'noticeType', 'title', 'contractingActivity', 'naicsCode', 
      'setAsideType', 'description', 'responseDeadline', 'pocName', 'pocEmail'
    ];
    
    const isValid = requiredFields.every(field => formData[field]);
    
    if (isValid) {
      return 'bg-green-100 text-green-800 border-green-200';
    } else {
      return 'bg-red-100 text-red-800 border-red-200';
    }
  };

  const getPreviewBadgeText = () => {
    const requiredFields = [
      'noticeType', 'title', 'contractingActivity', 'naicsCode', 
      'setAsideType', 'description', 'responseDeadline', 'pocName', 'pocEmail'
    ];
    
    const isValid = requiredFields.every(field => formData[field]);
    
    if (isValid) {
      return 'SAM.gov Ready';
    } else {
      return 'Required Fields Missing';
    }
  };

  return (
    <div className={`min-h-screen p-6 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'} transition-colors duration-200`}>
      
        {fromJA && (
          <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border mb-4 ${darkMode ? 'bg-blue-950/40 border-blue-700' : 'bg-blue-50 border-blue-200'}`}>
            <div>
              <p className={`text-sm font-semibold ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>Pre-filled from approved J&A</p>
              <p className={`text-xs ${darkMode ? 'text-blue-400' : 'text-blue-500'}`}>Fields carried over from your FAR 6.303 J&A. Review and complete before generating the SAM.gov notice.</p>
            </div>
            <button onClick={() => setFromJA(false)} className="ml-auto text-xs text-blue-400 hover:text-blue-600">Dismiss</button>
          </div>
        )}
<div className="max-w-7xl mx-auto">
        <h1 className={`text-3xl font-bold mb-8 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          SAM.gov Synopsis Generator
        </h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Panel - Form */}
          <div className={`rounded-lg shadow-lg p-6 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border`}>
            <h2 className={`text-xl font-semibold mb-6 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Pre-Solicitation Synopsis Form
            </h2>
            
            <div className="space-y-6">
              {/* Notice Type */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Notice Type *
                </label>
                <div className="space-y-2">
                  {noticeTypes.map((type) => (
                    <label key={type.value} className="flex items-center">
                      <input
                        type="radio"
                        name="noticeType"
                        value={type.value}
                        checked={formData.noticeType === type.value}
                        onChange={handleInputChange}
                        className={`mr-2 ${darkMode ? 'text-purple-500' : 'text-purple-600'}`}
                      />
                      <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{type.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Title */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Title / Requirement Name *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                    !isFieldValid('title') && showValidation 
                      ? 'border-red-500' 
                      : darkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>

              {/* Contracting Activity */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Contracting Activity *
                </label>
                <input
                  type="text"
                  name="contractingActivity"
                  value={formData.contractingActivity}
                  onChange={handleInputChange}
                  placeholder="Department of Veterans Affairs, VA Medical Center Denver"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                    !isFieldValid('contractingActivity') && showValidation 
                      ? 'border-red-500' 
                      : darkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>

              {/* NAICS Code and Description */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    NAICS Code *
                  </label>
                  <input
                    type="text"
                    name="naicsCode"
                    value={formData.naicsCode}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                      !isFieldValid('naicsCode') && showValidation 
                        ? 'border-red-500' 
                        : darkMode 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    NAICS Description
                  </label>
                  <input
                    type="text"
                    name="naicsDescription"
                    value={formData.naicsDescription}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                </div>
              </div>

              {/* Set-Aside Type */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Set-Aside Type *
                </label>
                <select
                  name="setAsideType"
                  value={formData.setAsideType}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                    !isFieldValid('setAsideType') && showValidation 
                      ? 'border-red-500' 
                      : darkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="">Select Set-Aside</option>
                  {setAsideTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Estimated Dollar Value */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Estimated Dollar Value
                </label>
                <input
                  type="text"
                  name="estimatedDollarValue"
                  value={formData.estimatedDollarValue}
                  onChange={handleInputChange}
                  placeholder="$1,200,000"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                    darkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>

              {/* Period of Performance */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Period of Performance
                </label>
                <input
                  type="text"
                  name="periodOfPerformance"
                  value={formData.periodOfPerformance}
                  onChange={handleInputChange}
                  placeholder="12 months base + 2 option years"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                    darkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>

              {/* Description */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Description of Requirement *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={4}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                    !isFieldValid('description') && showValidation 
                      ? 'border-red-500' 
                      : darkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>

              {/* Response Deadline */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Response Deadline *
                </label>
                <input
                  type="date"
                  name="responseDeadline"
                  value={formData.responseDeadline}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                    !isFieldValid('responseDeadline') && showValidation 
                      ? 'border-red-500' 
                      : darkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>

              {/* Response Time */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Response Due Time
                </label>
                <input
                  type="time"
                  name="responseTime"
                  value={formData.responseTime}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                    darkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>

              {/* Point of Contact */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Point of Contact Name *
                  </label>
                  <input
                    type="text"
                    name="pocName"
                    value={formData.pocName}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                      !isFieldValid('pocName') && showValidation 
                        ? 'border-red-500' 
                        : darkMode 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    POC Title
                  </label>
                  <input
                    type="text"
                    name="pocTitle"
                    value={formData.pocTitle}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    POC Email *
                  </label>
                  <input
                    type="email"
                    name="pocEmail"
                    value={formData.pocEmail}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                      !isFieldValid('pocEmail') && showValidation 
                        ? 'border-red-500' 
                        : darkMode 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    POC Phone
                  </label>
                  <input
                    type="text"
                    name="pocPhone"
                    value={formData.pocPhone}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                </div>
              </div>

              {/* VAAR/FAR Authority Note */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  VAAR/FAR Authority Note
                </label>
                <input
                  type="text"
                  name="vaarAuthorityNote"
                  value={formData.vaarAuthorityNote}
                  onChange={handleInputChange}
                  placeholder="VAAR 819.7003 - Veterans First Contracting Program"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                    darkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>

              {/* Additional Instructions */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Additional Instructions
                </label>
                <textarea
                  name="additionalInstructions"
                  value={formData.additionalInstructions}
                  onChange={handleInputChange}
                  rows={2}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                    darkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>

              {/* Buttons */}
              {/* ACUMEN-10 Live Synopsis Enhancement */}
              <div className={`rounded-xl border p-4 ${darkMode ? 'bg-gray-800/60 border-gray-700' : 'bg-blue-50 border-blue-200'}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>ACUMEN-10 Synopsis Enhancer</span>
                    <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 border border-purple-200 rounded-full">LIVE AI</span>
                  </div>
                  {synopsisAccepted && <span className="text-xs text-green-600 font-medium">✓ Approved</span>}
                </div>
                {!synopsisAiOutput && !synopsisLoading && (
                  <button onClick={async () => {
                    setSynopsisLoading(true);
                    try {
                      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/chat`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          message: `Review and enhance this SAM.gov pre-solicitation synopsis for FAR Part 5 compliance. Identify any gaps and provide an improved version ready for posting. Title: ${formData.title || 'VA Services Requirement'}, NAICS: ${formData.naics || '541512'}, Set-aside: ${formData.setAside || 'SDVOSB'}, Estimated value: ${formData.estimatedValue || 'TBD'}, Description: ${formData.description || 'See attached'}, Response date: ${formData.responseDate || 'TBD'}. Provide: (1) Compliance gaps found, (2) Enhanced synopsis text ready for SAM.gov.`,
                          context: 'SAM.gov pre-solicitation notice review for FAR 5.207 compliance'
                        })
                      });
                      const data = await res.json();
                      setSynopsisAiOutput(data.response || '');
                    } catch(e) { setSynopsisAiOutput('Error — check backend.'); }
                    setSynopsisLoading(false);
                  }} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700">
                    ⚡ Enhance with ACUMEN-10
                  </button>
                )}
                {synopsisLoading && <div className="flex items-center gap-2 py-2"><div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" /><span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>ACUMEN-10 reviewing synopsis...</span></div>}
                {synopsisAiOutput && !synopsisLoading && (
                  <div className="space-y-3">
                    <div className={`rounded-lg p-4 text-sm whitespace-pre-wrap overflow-auto max-h-64 ${darkMode ? 'bg-gray-900 text-gray-200 border border-gray-700' : 'bg-white text-gray-800 border border-gray-200'}`}>{synopsisAiOutput}</div>
                    <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Generated by ACUMEN-10 · Review before posting · CO approval required</p>
                    {!synopsisAccepted ? (
                      <div className="flex gap-2">
                        <button onClick={() => setSynopsisAccepted(true)} className="px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700">Approve for SAM.gov</button>
                        <button onClick={() => setSynopsisAiOutput('')} className={`px-4 py-1.5 text-sm font-medium rounded-lg border ${darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>Regenerate</button>
                      </div>
                    ) : (
                      <div className="px-3 py-2 bg-green-100 border border-green-200 rounded-lg"><p className="text-xs text-green-700 font-medium">Approved by CO — synopsis ready for SAM.gov posting</p></div>
                    )}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-3 pt-4">
                <button
                  onClick={generateNotice}
                  className={`px-4 py-2 rounded-md font-medium flex items-center gap-2 ${
                    darkMode 
                      ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                      : 'bg-purple-600 hover:bg-purple-700 text-white'
                  } transition-colors duration-200`}
                >
                  <FileText size={18} />
                  Generate Notice
                </button>
                
                {generatedNotice && (
                  <>
                    <button
                      onClick={copyToClipboard}
                      className={`px-4 py-2 rounded-md font-medium flex items-center gap-2 ${
                        darkMode 
                          ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                          : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                      } transition-colors duration-200`}
                    >
                      {copied ? <CheckCircle size={18} /> : <Copy size={18} />}
                      {copied ? 'Copied!' : 'Copy to Clipboard'}
                    </button>
                    
                    <button
                      onClick={downloadNotice}
                      className={`px-4 py-2 rounded-md font-medium flex items-center gap-2 ${
                        darkMode 
                          ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                          : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                      } transition-colors duration-200`}
                    >
                      <Download size={18} />
                      Download .txt
                    </button>
                    
                    <button
                      onClick={printNotice}
                      className={`px-4 py-2 rounded-md font-medium flex items-center gap-2 ${
                        darkMode 
                          ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                          : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                      } transition-colors duration-200`}
                    >
                      <Printer size={18} />
                      Print
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Right Panel - Preview */}
          <div className={`rounded-lg shadow-lg p-6 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border`}>
            <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              SAM.gov Notice Preview
            </h2>
            
            {generatedNotice ? (
              <>
                <div className={`mb-4 p-3 rounded-md border flex items-center justify-between ${
                  getPreviewBadgeColor()
                }`}>
                  <span className="font-medium">
                    {getPreviewBadgeText()}
                  </span>
                  {getPreviewBadgeText() === 'SAM.gov Ready' ? (
                    <CheckCircle size={20} />
                  ) : (
                    <AlertCircle size={20} />
                  )}
                </div>
                
                <div className={`p-4 rounded-md border mb-4 ${
                  darkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-300'
                }`}>
                  <pre className={`whitespace-pre-wrap font-mono text-sm ${
                    darkMode ? 'text-gray-300' : 'text-gray-800'
                  }`}>
                    {generatedNotice}
                  </pre>
                </div>
                
                <div className="flex justify-between text-sm">
                  <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                    Characters: {getCharacterCount(generatedNotice)}
                  </span>
                  <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                    Words: {getWordCount(generatedNotice)}
                  </span>
                </div>
              </>
            ) : (
              <div className={`p-8 text-center rounded-md border ${
                darkMode ? 'bg-gray-900 border-gray-700 text-gray-500' : 'bg-gray-50 border-gray-300 text-gray-500'
              }`}>
                <FileText size={48} className="mx-auto mb-4 opacity-50" />
                <p>Fill out the form and click "Generate Notice" to see preview</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SynopsisGenerator;
